import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { PaginacaoComponent } from '../../../../../shared/components/paginacao/paginacao.component';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { salvarArquivo } from '../../../../../shared/utils/arquivo.util';
import {
  ArquivoMinhaSolicitacao,
  ErroCancelamentoSolicitacao,
  MinhaSolicitacao,
} from '../../../models/minhas-solicitacoes.model';
import {
  mapearErroCancelamento,
  MinhasSolicitacoesService,
  paraMinhaSolicitacao,
} from '../../../services/minhas-solicitacoes.service';
import {
  mensagemErroDownloadAnexo,
  SolicitacaoServicoPublicaService,
} from '../../../services/solicitacao-servico-publica.service';
import {
  AnexoRespostaSolicitacao,
  formatarData,
  normalizarProtocolo,
  RespostaSolicitacao,
  STATUS_SOLICITACAO,
  StatusOpcao,
  StatusSolicitacao,
} from '../../../services/solicitacoes.service';
import { ComplementoSolicitacaoComponent } from '../../acompanhar-solicitacao-deslogado/detalhe-solicitacao/complemento-solicitacao/complemento-solicitacao.component';
import { ComplementoPedidoSicComponent } from '../../acompanhar-solicitacao-deslogado/detalhe-solicitacao/complemento-pedido-sic/complemento-pedido-sic.component';
import { MOTIVOS_CONTESTACAO_SIC, paraMeuPedidoSic } from '../../../models/pedido-sic-acompanhamento.model';
import { mapearErroContestacao, MeusPedidosSicService } from '../../../services/meus-pedidos-sic.service';

interface Filtros {
  protocolo: string;
  servico: string;
  status: string;
  dataInicial: string;
  dataFinal: string;
}

const FILTROS_VAZIOS: Filtros = {
  protocolo: '',
  servico: '',
  status: '',
  dataInicial: '',
  dataFinal: '',
};

/** O que a aba mostra no lugar da lista — ou, em `pronto`, a própria lista. */
type EstadoSolicitacoes = 'sessao' | 'carregando' | 'falha' | 'vazia' | 'pronto';

/** Qual dos quatro `<dialog>` está aberto: o conteúdo de cada um só existe enquanto ele está. */
type ModalAberto = 'detalhes' | 'resposta' | 'cancelamento' | 'contestacao';

/** A fonte que falhou sozinha: a aba mostra a outra e um aviso com "Tentar novamente". */
type FonteSolicitacoes = 'servicos' | 'sic';

/** O limite da especificação do motivo *Outros* e o da justificativa — os do servidor. */
const LIMITE_MOTIVO_OUTRO = 250;
const LIMITE_JUSTIFICATIVA = 4000;

/** A falha do último download, presa ao arquivo que falhou (do cidadão ou da resposta). */
interface ErroDownload {
  chave: string;
  mensagem: string;
}

/**
 * As solicitações de quem está logado, numa lista só: as de serviço digital — as SEM
 * agendamento, que "Meus agendamentos" não mostra — e os pedidos do e-SIC (change
 * portal-consulta-pedido-sic), com o detalhe, a resposta, o complemento, o cancelamento e, no
 * e-SIC, a contestação.
 *
 * As duas listas vêm inteiras das APIs, já com o que os modais mostram, e são compostas aqui
 * pela abertura; filtros e paginação ficam na tela. As duas fontes são **independentes**: a falha
 * de uma não derruba a outra. O que cada item pode fazer vem pronto da API (`podeCancelar`,
 * `podeContestar`).
 *
 * ⚠️ Os quatro modais são `<dialog>` com `showModal()`: o SweetAlert abriria atrás deles, inerte.
 * Por isso os erros ficam dentro do modal, e o alerta de sucesso só sai depois do `close()`.
 */
@Component({
  selector: 'app-minhas-solicitacoes',
  templateUrl: './minhas-solicitacoes.component.html',
  styleUrls: ['./minhas-solicitacoes.component.css'],
  imports: [LucideModule, PaginacaoComponent, RouterLink, ComplementoSolicitacaoComponent, ComplementoPedidoSicComponent],
  host: {
    '(document:click)': 'fecharMenu()',
    '(document:keydown.escape)': 'fecharMenu()',
  },
})
export class MinhasSolicitacoesComponent {
  private readonly minhasSolicitacoes = inject(MinhasSolicitacoesService);
  private readonly meusPedidosSic = inject(MeusPedidosSicService);
  private readonly solicitacaoServicoPublica = inject(SolicitacaoServicoPublicaService);
  private readonly alerta = inject(AlertaService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly modalDetalhes = viewChild<ElementRef<HTMLDialogElement>>('modalDetalhes');
  private readonly modalResposta = viewChild<ElementRef<HTMLDialogElement>>('modalResposta');
  private readonly modalCancelamento = viewChild<ElementRef<HTMLDialogElement>>('modalCancelamento');
  private readonly modalContestacao = viewChild<ElementRef<HTMLDialogElement>>('modalContestacao');

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  /**
   * Distingue "sessão encerrada" de "falha": num 401 o `ErrorInterceptor` faz logout, e a aba
   * continua na tela.
   */
  private readonly logado = computed(() => !!this.usuario()?.token);

  /** O botão "⋮" do item cujo menu abriu o modal: é para ele que o foco volta. */
  private botaoDoMenu: HTMLElement | null = null;

  readonly menuAberto = signal('');
  readonly modalAberto = signal<ModalAberto | null>(null);
  readonly protocoloSelecionado = signal<string | null>(null);
  readonly motivoCancelamento = signal('');

  /**
   * Getter, e não campo: no bundle da suíte de testes o catálogo ainda não está inicializado
   * quando o componente é construído (o campo nascia `undefined`), e só na hora do uso está.
   */
  get statusDisponiveis(): readonly StatusOpcao[] {
    return STATUS_SOLICITACAO;
  }

  /**
   * As duas fontes numa lista só, da abertura (data e hora) mais recente para a mais antiga — o
   * protocolo desempata, para a ordem não depender da fonte.
   */
  readonly todas = computed<readonly MinhaSolicitacao[]>(() =>
    [
      ...(this.minhasSolicitacoes.solicitacoes() ?? []).map(paraMinhaSolicitacao),
      ...(this.meusPedidosSic.lista() ?? []).map(paraMeuPedidoSic),
    ].sort(
      (a, b) =>
        `${b.abertaEm}T${b.abertaAs}`.localeCompare(`${a.abertaEm}T${a.abertaAs}`) || b.protocolo.localeCompare(a.protocolo),
    ),
  );

  /**
   * `falha` só quando as DUAS fontes falham; `carregando` até as duas responderem (ou uma falhar
   * e a outra responder).
   */
  readonly estado = computed<EstadoSolicitacoes>(() => {
    if (!this.logado()) return 'sessao';

    const falhouServicos = this.minhasSolicitacoes.falhou();
    const falhouSic = this.meusPedidosSic.falhou();

    if (falhouServicos && falhouSic) return 'falha';

    const aguardandoServicos = !falhouServicos && this.minhasSolicitacoes.solicitacoes() === null;
    const aguardandoSic = !falhouSic && this.meusPedidosSic.lista() === null;

    if (aguardandoServicos || aguardandoSic) return 'carregando';

    return this.todas().length === 0 ? 'vazia' : 'pronto';
  });

  /** A fonte que falhou sozinha — a aba mostra a outra e o aviso desta. */
  readonly falhaParcial = computed<FonteSolicitacoes | null>(() => {
    if (this.estado() !== 'vazia' && this.estado() !== 'pronto') return null;
    if (this.minhasSolicitacoes.falhou()) return 'servicos';
    if (this.meusPedidosSic.falhou()) return 'sic';

    return null;
  });

  /** Os nomes de serviço que aparecem na lista, sem repetição: as opções do filtro "Serviço". */
  readonly servicos = computed<readonly string[]>(() =>
    [...new Set(this.todas().map((solicitacao) => solicitacao.servico))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
  );

  /** Presos às opções: o serviço que sai da lista relida deixa de filtrar. */
  readonly rascunho = linkedSignal<readonly string[], Filtros>({
    source: this.servicos,
    computation: (servicos, anterior) => semServicoAusente(anterior?.value ?? FILTROS_VAZIOS, servicos),
  });

  readonly filtros = linkedSignal<readonly string[], Filtros>({
    source: this.servicos,
    computation: (servicos, anterior) => semServicoAusente(anterior?.value ?? FILTROS_VAZIOS, servicos),
  });

  readonly itensPorPagina = signal(10);

  readonly solicitacoesFiltradas = computed<readonly MinhaSolicitacao[]>(() => {
    const { protocolo, servico, status, dataInicial, dataFinal } = this.filtros();
    const procurado = normalizarProtocolo(protocolo);

    return this.todas().filter((solicitacao) => {
      if (procurado && !normalizarProtocolo(solicitacao.protocolo).includes(procurado)) return false;
      if (servico && solicitacao.servico !== servico) return false;
      if (status && solicitacao.status !== status) return false;
      if (dataInicial && solicitacao.abertaEm < dataInicial) return false;
      if (dataFinal && solicitacao.abertaEm > dataFinal) return false;

      return true;
    });
  });

  readonly total = computed(() => this.solicitacoesFiltradas().length);

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.total() / this.itensPorPagina()))
  );

  readonly paginaAtual = linkedSignal({
    source: () => ({ filtros: this.filtros(), porPagina: this.itensPorPagina() }),
    computation: () => 1,
  });

  readonly solicitacoesDaPagina = computed<readonly MinhaSolicitacao[]>(() => {
    const inicio = (this.paginaAtual() - 1) * this.itensPorPagina();

    return this.solicitacoesFiltradas().slice(inicio, inicio + this.itensPorPagina());
  });

  readonly primeiroDaPagina = computed(() =>
    this.total() === 0 ? 0 : (this.paginaAtual() - 1) * this.itensPorPagina() + 1
  );

  readonly ultimoDaPagina = computed(() =>
    Math.min(this.paginaAtual() * this.itensPorPagina(), this.total())
  );

  readonly temFiltroAtivo = computed(() =>
    Object.values(this.filtros()).some((valor) => valor !== '')
  );

  /**
   * A solicitação do modal aberto, procurada na lista ATUAL: depois de um complemento ou de um
   * cancelamento a lista é relida, e o modal mostra os dados novos.
   */
  readonly selecionada = computed<MinhaSolicitacao | null>(() => {
    const protocolo = this.protocoloSelecionado();

    return this.todas().find((solicitacao) => solicitacao.protocolo === protocolo) ?? null;
  });

  /** As respostas do órgão, da mais **recente** para a mais antiga — a API as traz na ordem inversa. */
  readonly respostasExibidas = computed<readonly RespostaSolicitacao[]>(() =>
    [...(this.selecionada()?.respostas ?? [])].reverse(),
  );

  readonly respostaMaisRecente = computed<RespostaSolicitacao | null>(() => this.respostasExibidas()[0] ?? null);

  /** A chave do arquivo sendo baixado (`cidadao-12`, `resposta-34`). Um por vez. */
  readonly baixando = signal<string | null>(null);
  readonly erroDownload = signal<ErroDownload | null>(null);

  /** Um cancelamento em curso: trava o segundo clique. */
  readonly cancelando = signal(false);
  readonly erroCancelamento = signal<ErroCancelamentoSolicitacao | null>(null);

  /** O aviso do complemento enviado, dentro do detalhe. */
  readonly avisoComplemento = signal('');

  /** Getter pelo mesmo motivo do `statusDisponiveis`: no bundle da suíte o catálogo nasce depois. */
  get motivosContestacao(): readonly { chave: string; rotulo: string }[] {
    return MOTIVOS_CONTESTACAO_SIC;
  }

  readonly limiteMotivoOutro = LIMITE_MOTIVO_OUTRO;
  readonly limiteJustificativa = LIMITE_JUSTIFICATIVA;

  /** O formulário da contestação: motivo pela chave, especificação e justificativa. */
  readonly contestacao = signal({ motivo: '', motivoOutro: '', justificativa: '' });
  readonly tentouContestar = signal(false);
  readonly contestando = signal(false);
  readonly erroContestacao = signal<ErroCancelamentoSolicitacao | null>(null);

  readonly erroMotivoContestacao = computed(() =>
    this.tentouContestar() && !this.contestacao().motivo ? 'Escolha o motivo da contestação.' : '',
  );

  readonly erroMotivoOutro = computed(() => {
    if (!this.tentouContestar() || this.contestacao().motivo !== 'Outros') return '';

    return this.contestacao().motivoOutro.trim() ? '' : 'Especifique o motivo da contestação.';
  });

  /** Obrigatória só quando o canal em destaque exige — a flag vem no pedido. */
  readonly exigeJustificativa = computed(() => !!this.selecionada()?.sic?.justificativaObrigatoria);

  readonly erroJustificativa = computed(() =>
    this.tentouContestar() && this.exigeJustificativa() && !this.contestacao().justificativa.trim()
      ? 'A justificativa é obrigatória para contestar a resposta deste pedido.'
      : '',
  );

  /** O título das respostas, pela categoria do item. */
  readonly tituloResposta = computed(() =>
    this.selecionada()?.categoria === 'sic' ? 'Resposta do e-SIC' : 'Resposta do órgão',
  );

  constructor() {
    this.minhasSolicitacoes.carregar();
    this.meusPedidosSic.carregar();

    // Sessão encerrada com um modal aberto: ele não pode ficar mostrando a solicitação de quem
    // saiu por cima do aviso de sessão.
    effect(() => {
      if (this.logado()) return;

      untracked(() => this.fecharModais());
    });
  }

  aoDigitar(campo: keyof Filtros, evento: Event): void {
    const valor = (evento.target as HTMLInputElement | HTMLSelectElement).value;
    this.rascunho.update((atual) => ({ ...atual, [campo]: valor }));
  }

  filtrar(): void {
    this.filtros.set({ ...this.rascunho() });
  }

  limparFiltros(): void {
    this.rascunho.set({ ...FILTROS_VAZIOS });
    this.filtros.set({ ...FILTROS_VAZIOS });
  }

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);
  }

  aoTrocarItensPorPagina(evento: Event): void {
    this.itensPorPagina.set(Number((evento.target as HTMLSelectElement).value));
  }

  /** O "Tentar novamente" da falha total: refaz as duas. */
  tentarNovamente(): void {
    this.minhasSolicitacoes.recarregar();
    this.meusPedidosSic.recarregar();
  }

  /** O "Tentar novamente" da falha parcial: refaz só a fonte que falhou. */
  tentarNovamenteFonte(fonte: FonteSolicitacoes): void {
    if (fonte === 'sic') {
      this.meusPedidosSic.recarregar();
    } else {
      this.minhasSolicitacoes.recarregar();
    }
  }

  rotuloStatus(status: StatusSolicitacao): string {
    return this.statusDisponiveis.find((opcao) => opcao.id === status)?.rotulo ?? '';
  }

  /** O selo: no e-SIC o rótulo do back-end (um dos dez); na Carta de Serviço o grupo de tela. */
  rotuloItem(solicitacao: MinhaSolicitacao): string {
    return solicitacao.sic?.statusDescricao ?? this.rotuloStatus(solicitacao.status);
  }

  iconeStatus(status: StatusSolicitacao): string {
    return this.statusDisponiveis.find((opcao) => opcao.id === status)?.icone ?? 'clock';
  }

  dataFormatada(iso: string): string {
    return formatarData(iso);
  }

  alternarMenu(protocolo: string, evento: Event): void {
    evento.stopPropagation();
    this.botaoDoMenu = evento.currentTarget as HTMLElement;
    this.menuAberto.update((atual) => (atual === protocolo ? '' : protocolo));
  }

  fecharMenu(): void {
    this.menuAberto.set('');
  }

  verDetalhes(solicitacao: MinhaSolicitacao): void {
    this.avisoComplemento.set('');
    this.abrir('detalhes', solicitacao, this.modalDetalhes());
  }

  verResposta(solicitacao: MinhaSolicitacao): void {
    this.abrir('resposta', solicitacao, this.modalResposta());
  }

  abrirCancelamento(solicitacao: MinhaSolicitacao): void {
    this.motivoCancelamento.set('');
    this.erroCancelamento.set(null);
    this.abrir('cancelamento', solicitacao, this.modalCancelamento());
  }

  abrirContestacao(solicitacao: MinhaSolicitacao): void {
    this.contestacao.set({ motivo: '', motivoOutro: '', justificativa: '' });
    this.tentouContestar.set(false);
    this.erroContestacao.set(null);
    this.abrir('contestacao', solicitacao, this.modalContestacao());
  }

  aoEscolherMotivo(motivo: string): void {
    this.contestacao.update((atual) => ({ ...atual, motivo, motivoOutro: motivo === 'Outros' ? atual.motivoOutro : '' }));
  }

  aoDigitarContestacao(campo: 'motivoOutro' | 'justificativa', evento: Event): void {
    const valor = (evento.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.contestacao.update((atual) => ({ ...atual, [campo]: valor }));
  }

  /**
   * Envia a contestação. O que o modal sabe validar é conferido antes — motivo, especificação de
   * *Outros* e a justificativa que o canal exige —; o resto (janela, instâncias) é do servidor.
   */
  confirmarContestacao(evento: Event): void {
    evento.preventDefault();

    const solicitacao = this.selecionada();

    if (!solicitacao || this.contestando()) return;

    this.tentouContestar.set(true);
    this.erroContestacao.set(null);

    if (this.erroMotivoContestacao() || this.erroMotivoOutro() || this.erroJustificativa()) return;

    const { motivo, motivoOutro, justificativa } = this.contestacao();

    this.contestando.set(true);

    this.meusPedidosSic
      .contestar(solicitacao.protocolo, {
        motivo,
        motivoOutro: motivo === 'Outros' ? motivoOutro.trim() : null,
        justificativa: justificativa.trim() || null,
      })
      .pipe(
        finalize(() => this.contestando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          // O modal fecha ANTES: o SweetAlert abriria atrás dele, inerte.
          this.fecharModais();

          void this.alerta.sucesso({
            titulo: 'Contestação enviada',
            html: `O recurso do protocolo <b>${solicitacao.protocolo}</b> foi registrado.`,
          });
        },
        error: (erro: unknown) => {
          const falha = mapearErroContestacao(erro);

          this.erroContestacao.set(falha);

          if (falha.recarregar) this.meusPedidosSic.recarregar();
        },
      });
  }

  /**
   * Fecha o modal aberto e devolve o foco ao "⋮" do item: o item de menu que o abriu não existe
   * mais. Também é o tratador do evento `close` do `<dialog>` — o `Esc` só avisa por ele.
   */
  fecharModais(): void {
    for (const modal of [this.modalDetalhes(), this.modalResposta(), this.modalCancelamento(), this.modalContestacao()]) {
      if (modal?.nativeElement.open) modal.nativeElement.close();
    }

    if (this.modalAberto() === null) return;

    this.modalAberto.set(null);
    this.protocoloSelecionado.set(null);
    this.baixando.set(null);
    this.erroDownload.set(null);

    const botao = this.botaoDoMenu;

    this.botaoDoMenu = null;

    if (botao?.isConnected) botao.focus();
  }

  aoDigitarMotivo(evento: Event): void {
    this.motivoCancelamento.set((evento.target as HTMLTextAreaElement).value);
  }

  confirmarCancelamento(evento: Event): void {
    evento.preventDefault();

    const solicitacao = this.selecionada();
    const motivo = this.motivoCancelamento().trim();

    if (!solicitacao || !motivo || this.cancelando()) return;

    this.cancelando.set(true);
    this.erroCancelamento.set(null);

    // O serviço é o da CATEGORIA do item: a aba é uma só, as rotas não.
    const cancelamento$: Observable<unknown> =
      solicitacao.categoria === 'sic'
        ? this.meusPedidosSic.cancelar(solicitacao.protocolo, motivo)
        : this.minhasSolicitacoes.cancelar(solicitacao.protocolo, motivo);

    cancelamento$
      .pipe(
        finalize(() => this.cancelando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          // O modal fecha ANTES: o SweetAlert abriria atrás dele, inerte.
          this.fecharModais();

          void this.alerta.sucesso({
            titulo: 'Solicitação cancelada',
            html: `O protocolo <b>${solicitacao.protocolo}</b> foi cancelado.`,
          });
        },
        error: (erro: unknown) => {
          const falha = mapearErroCancelamento(erro);

          this.erroCancelamento.set(falha);

          if (!falha.recarregar) return;

          if (solicitacao.categoria === 'sic') {
            this.meusPedidosSic.recarregar();
          } else {
            this.minhasSolicitacoes.recarregar();
          }
        },
      });
  }

  /** Baixa um anexo que o próprio cidadão enviou, pela rota autenticada do dono. */
  baixarArquivo(arquivo: ArquivoMinhaSolicitacao): void {
    const protocolo = this.selecionada()?.protocolo;

    if (!protocolo) return;

    this.baixar(`cidadao-${arquivo.codigo}`, arquivo.nome, this.minhasSolicitacoes.baixarAnexo(protocolo, arquivo.codigo));
  }

  /** Baixa um anexo da resposta do órgão, pela rota pública do protocolo. */
  baixarAnexoResposta(anexo: AnexoRespostaSolicitacao): void {
    const protocolo = this.selecionada()?.protocolo;

    if (!protocolo) return;

    this.baixar(
      `resposta-${anexo.codigo}`,
      anexo.nome,
      this.solicitacaoServicoPublica.baixarAnexoResposta(protocolo, anexo.codigo),
    );
  }

  /** A resposta ao complemento do e-SIC sempre retoma o prazo, que estava suspenso. */
  aoEnviarComplementoSic(): void {
    this.avisoComplemento.set('Complemento enviado. O prazo voltou a contar.');
    this.meusPedidosSic.recarregar();
  }

  /** Só promete o prazo correndo de novo quando o pedido do órgão o tinha pausado. */
  aoEnviarComplemento(pausaPrazo: boolean): void {
    this.avisoComplemento.set(
      pausaPrazo
        ? 'Complemento enviado. O órgão foi avisado e o prazo voltou a contar.'
        : 'Complemento enviado. O órgão foi avisado.',
    );

    this.minhasSolicitacoes.recarregar();
  }

  private abrir(qual: ModalAberto, solicitacao: MinhaSolicitacao, modal: ElementRef<HTMLDialogElement> | undefined): void {
    this.protocoloSelecionado.set(solicitacao.protocolo);
    this.modalAberto.set(qual);
    this.baixando.set(null);
    this.erroDownload.set(null);
    this.fecharMenu();
    modal?.nativeElement.showModal();
  }

  private baixar(chave: string, nome: string, arquivo$: Observable<Blob>): void {
    if (this.baixando() !== null) return;

    this.baixando.set(chave);
    this.erroDownload.set(null);

    arquivo$
      .pipe(
        finalize(() => this.baixando.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (blob) => salvarArquivo(blob, nome),
        error: (erro: unknown) => this.erroDownload.set({ chave, mensagem: mensagemErroDownloadAnexo(erro) }),
      });
  }
}

/** O filtro sem o serviço que não está mais entre as opções. */
function semServicoAusente(filtros: Filtros, servicos: readonly string[]): Filtros {
  return !filtros.servico || servicos.includes(filtros.servico) ? filtros : { ...filtros, servico: '' };
}
